import type { FontConfig } from '@shared/app-config/types'

const GOOGLE_CSS2 = 'https://fonts.googleapis.com/css2'

/** Strip characters that would escape a CSS string/url context (quoted values). */
function sanitize(value: string): string {
  return value.replace(/['"\\)]/g, '')
}

/**
 * Sanitize an UNQUOTED CSS descriptor value (font-weight, font-style,
 * unicode-range, the metric overrides, font-display). These are interpolated
 * straight into declaration context, so — unlike quoted strings, where braces
 * are inert — a stray `;`/`}` would break out of the @font-face block / url().
 * Allow only the characters those descriptors legitimately use
 * (`100 900`, `italic`, `U+0000-00FF`, `95%`, `swap`, …); drop everything else.
 */
function sanitizeValue(value: string): string {
  return value.replace(/[^\w %.,+-]/g, '')
}

function googleImport(font: FontConfig): string {
  const family = sanitize(font.family.trim()).replace(/ /g, '+')
  let axis = ''
  if (font.weights?.length) {
    const weights = [...font.weights].sort((a, b) => a - b)
    // css2 axis tuples must be sorted: all normal weights, then all italics
    axis = font.italics
      ? `:ital,wght@${[...weights.map(w => `0,${w}`), ...weights.map(w => `1,${w}`)].join(';')}`
      : `:wght@${weights.join(';')}`
  }
  else if (font.italics) {
    axis = ':ital@0;1'
  }
  return `@import url('${GOOGLE_CSS2}?family=${family}${axis}&display=${sanitizeValue(font.display ?? 'swap')}');`
}

function faceBlocks(font: FontConfig): string {
  const family = sanitize(font.family)
  return (font.faces ?? [])
    .map((face) => {
      const src = `url('${sanitize(face.url)}')${face.format ? ` format('${sanitize(face.format)}')` : ''}`
      const lines = [
        `font-family: '${family}';`,
        `src: ${src};`,
        face.weight ? `font-weight: ${sanitizeValue(face.weight)};` : '',
        face.style ? `font-style: ${sanitizeValue(face.style)};` : '',
        face.unicodeRange ? `unicode-range: ${sanitizeValue(face.unicodeRange)};` : '',
        `font-display: ${sanitizeValue(font.display ?? 'swap')};`,
        font.sizeAdjust ? `size-adjust: ${sanitizeValue(font.sizeAdjust)};` : '',
        font.ascentOverride ? `ascent-override: ${sanitizeValue(font.ascentOverride)};` : '',
        font.descentOverride ? `descent-override: ${sanitizeValue(font.descentOverride)};` : '',
        font.lineGapOverride ? `line-gap-override: ${sanitizeValue(font.lineGapOverride)};` : '',
      ].filter(Boolean)
      return `@font-face {\n  ${lines.join('\n  ')}\n}`
    })
    .join('\n')
}

/**
 * Compile declarative `fonts` theming config into plain CSS: css2 @imports
 * first (CSS requires @import before other rules), then generated @font-face
 * blocks, then role wiring (`--p-font-<role>-family` vars; a `body` rule for
 * the body role, which has no default token consumer).
 *
 * Metric-override descriptors (size-adjust / ascent-override / …) only exist
 * at the @font-face level, so they apply to `faces` sources — a css2 response
 * can't be amended. Returns '' when there is nothing to emit.
 */
export function buildFontCss(fonts: FontConfig[] | undefined): string {
  if (!fonts?.length)
    return ''

  const imports: string[] = []
  const faces: string[] = []
  const roleVars: string[] = []
  const rules: string[] = []

  for (const font of fonts) {
    if (!font?.family)
      continue
    if (font.source === 'google')
      imports.push(googleImport(font))
    else if (font.source === 'faces' && font.faces?.length)
      faces.push(faceBlocks(font))

    for (const role of font.roles ?? []) {
      roleVars.push(`--p-font-${role}-family: '${sanitize(font.family)}';`)
      if (role === 'body')
        rules.push(`body { font-family: var(--p-font-body-family), ui-sans-serif, system-ui, sans-serif; }`)
    }
  }

  const parts = [...imports, ...faces]
  if (roleVars.length)
    parts.push(`:root {\n  ${roleVars.join('\n  ')}\n}`)
  parts.push(...rules)
  return parts.filter(Boolean).join('\n')
}
